import React, { ReactNode, useEffect, useMemo, ComponentType } from 'react';

import { getAdminApiUrl } from '@adsk/acs-sc-web-platform-api';
import { CONTEXT_NAMES } from '@adsk/alloy-helpers';
import {
  getOrCreateGlobalContext,
  useStoreWithValidation,
} from '@adsk/alloy-react-context';

import { PROJECT_CLASSIFICATION_TYPES } from './consts';
import { useCurrentProjectId } from './CurrentProjectIdContext';
import isProjectTemplate from './helpers/isProjectTemplate';
import { useProviderOnlyOnce } from './hooks/useProviderOnlyOnce';
import {
  EntitlementsProject,
  EntitlementsUser,
} from './projectEntitlementTypes';
import { useProjectEntitlementsStore } from './stores/ProjectEntitlementsStore';

export type ProjectEntitlementsContextValue = {
  loading: boolean;
  error?: false | Error;
  retry(): Promise<void>;
  currentProject?: EntitlementsProject;
  user?: EntitlementsUser;
  setUser(user: EntitlementsUser): void;
  setCurrentProject(currentProject: EntitlementsProject): void;
};

/**
 * Context for project entitlements.
 */
export const ProjectEntitlementsContext =
  getOrCreateGlobalContext<ProjectEntitlementsContextValue>(
    CONTEXT_NAMES.ProjectEntitlementsContext,
  );

/**
 * Returns the full project entitlements context value. Must be used inside `ProjectEntitlementsProvider`.
 */
export const useProjectEntitlements = () =>
  useStoreWithValidation<ProjectEntitlementsContextValue>(
    CONTEXT_NAMES.ProjectEntitlementsContext,
    {
      hook: 'useProjectEntitlements',
      provider: 'ProjectEntitlementsProvider',
      context: ProjectEntitlementsContext,
    },
  );

/**
 * Returns the current project from entitlements. Must be used inside `ProjectEntitlementsProvider`.
 * Returns `undefined` if project entitlements are not yet available.
 * @returns The current project or undefined
 * @example
 * import { useCurrentProject } from '@adsk/alloy-provider';
 * ...
 * const currentProject = useCurrentProject();
 */
export const useCurrentProject = () => {
  const { currentProject } = useProjectEntitlements();
  return currentProject;
};

/**
 * Returns the current logged-in user and a setter to update it. Must be used inside `ProjectEntitlementsProvider`.
 * Returns an empty object if project entitlements are not yet available.
 * @example
 * import { useUser } from '@adsk/alloy-provider';
 * ...
 * const { user, setUser } = useUser();
 */
export const useUser = () => {
  const { user, setUser } = useProjectEntitlements();
  return useMemo(() => (user ? { user, setUser } : {}), [user, setUser]);
};

/**
 * Returns whether the current project is a template. Must be used inside `ProjectEntitlementsProvider`.
 * @example
 * import { useCurrentProjectIsTemplate } from '@adsk/alloy-provider';
 * ...
 * const isTemplate = useCurrentProjectIsTemplate();
 */
export const useCurrentProjectIsTemplate = () => {
  const currentProject = useCurrentProject();
  return currentProject ? isProjectTemplate(currentProject) : false;
};

const fields = [
  'accountId',
  'name',
  'accountName',
  'accountImageUrl',
  'defaultUrl',
  'classification',
  'imageUrl',
  'region',
  'products',
  'platform',
  'mfaBlocked',
].join(',');

const classificationFilter = `filter[classification]=${Object.values(
  PROJECT_CLASSIFICATION_TYPES,
).join(',')}`;

const getCurrentProjectIdPart = (projectId: string | undefined) =>
  projectId ? `currentProjectId=${projectId}&` : '';

export const useProjectEntitlementsProvider = () => {
  const projectId = useCurrentProjectId();
  // Always request blocked products & tools. The backend gates this purely on
  // the query param (there is no backend feature flag), and consumers need the
  // blocked-tool authorization info to redirect correctly (e.g. to
  // /access?type=toolBlock). Keeping the URL constant means the SWR/store cache
  // key never changes mid-load, so the entitlements endpoint is fetched once.
  const url = useMemo(
    () =>
      `${getAdminApiUrl()}/ui/v1/clients?${getCurrentProjectIdPart(
        projectId,
      )}fields=${fields}&${classificationFilter}&limit=1&includeBlockedProductsAndTools=true`,
    [projectId],
  );

  const {
    error,
    loading,
    fetchData,
    retry,
    setUser,
    setCurrentProject,
    user,
    currentProject,
  } = useProjectEntitlementsStore();

  useEffect(() => {
    if (url) {
      fetchData(url);
    }
  }, [fetchData, url]);

  useEffect(() => {
    if (
      error &&
      !((error as unknown) instanceof Error) &&
      error.status !== 401
    ) {
      // 401 errors are pretty common and expected, here we want to mostly log unexpected ones
      console.error('Error calling project entitlements', error);
    }
  }, [error]);

  return useMemo(
    () => ({
      ...(error && { error }),
      loading,
      retry,
      user,
      setUser,
      currentProject,
      setCurrentProject,
    }),
    [error, loading, retry, user, setUser, currentProject, setCurrentProject],
  );
};

type ProjectEntitlementsProviderProps = {
  children?: ReactNode;
};

export const ProjectEntitlementsProvider: ComponentType<
  ProjectEntitlementsProviderProps
> = ({ children }) => {
  const projectEntitlementsContextValue = useProjectEntitlementsProvider();

  useProviderOnlyOnce({
    context: ProjectEntitlementsContext,
    contextName: CONTEXT_NAMES.ProjectEntitlementsContext,
  });

  return (
    <ProjectEntitlementsContext.Provider
      value={projectEntitlementsContextValue}
    >
      {children}
    </ProjectEntitlementsContext.Provider>
  );
};

ProjectEntitlementsProvider.displayName = 'ProjectEntitlementsProvider';
